import PurposeBadge from "../../components/ui/PurposeBadge";
import "./TransactionDetailsModal.css";
import { enumLabel } from "../../components/ui/enumLabel";
import { confirmAction } from "../../components/ui/confirmAction";
import Button from "../../components/ui/Button";
import React, { useEffect, useState } from "react";
import { Transaction } from "../../types/transaction";
import { getTransactionById } from "../../services/transactionService";
import { downloadTransactionReceipt, refundWallet } from "../../services/financeService";
import { getApiErrorMessage } from "../../utils/apiError";
import { saveDownload, timestampPdfFilename } from "../../utils/download";
import { formatMoney } from "../../utils/formatMoney";
import { getCustomerDisplayName } from "../../utils/customerDisplay";
import { useAccess } from "../../hooks/useAccess";
import Modal from "../Modal/Modal";

export default function TransactionDetailsModal({ isOpen, onClose, transaction, onUpdated }: { isOpen: boolean; onClose: () => void; transaction: Transaction; onUpdated?: () => void }) {
  const { can } = useAccess();
  const [detail, setDetail] = useState<Transaction | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (!isOpen) return;
    const controller = new AbortController(); setLoading(true); setError("");
    getTransactionById(transaction.id, controller.signal).then(result => {
      if (controller.signal.aborted) return;
      setDetail(result.data.transaction);
      if (result.data.transaction.paymentStatus.toLowerCase() === "refunded") { setUncertain(false); setMessage("The wallet refund is confirmed."); }
    }).catch(err => { if (!controller.signal.aborted) setError(getApiErrorMessage(err)); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [isOpen, transaction.id, version]);
  if (!isOpen) return null;
  const userId = detail?.userId || detail?.user?.id;
  const walletId = detail?.walletId || detail?.wallet?.id;
  const refundable = detail?.paymentStatus.toLowerCase() === "successful" && detail.transactionType?.toLowerCase() === "debit" && !!walletId && !!userId && !!detail.transactionReference;
  const refund = async () => {
    if (!detail || !walletId || !userId || !await confirmAction(`Refund this wallet debit of ${formatMoney(detail.amount ?? detail.amountPaid, detail.currency)}? This credits the customer's wallet.`)) return;
    setBusy(true); setError(""); setMessage("");
    try {
      const result = await refundWallet({ userId, walletId, transactionTxRef: detail.transactionReference });
      if (!result.success) throw new Error("The refund was not confirmed. Reload the transaction to check its outcome.");
      setDetail({ ...detail, paymentStatus: "refunded" });
      setMessage(`Wallet refund confirmed. Balance: ${formatMoney(result.wallet.walletBalance, detail.currency)}.`);
      onUpdated?.();
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      setUncertain(!status || status >= 500);
      setError(getApiErrorMessage(err) + (!status || status >= 500 ? " The outcome is uncertain. Reload the transaction to check its status." : ""));
    } finally { setBusy(false); }
  };
  const download = async () => {
    if (!detail?.id) return; setBusy(true); setError("");
    try { saveDownload(await downloadTransactionReceipt(detail.id), timestampPdfFilename()); }
    catch (err) { setError(getApiErrorMessage(err)); } finally { setBusy(false); }
  };
  return <Modal size="medium" title="Transaction details" onClose={() => { if (!busy) onClose(); }}>
    {error && <p role="alert" className="integration-error">{error}</p>}
    {message && <p role="status" className="integration-success">{message}</p>}
    <Button disabled={busy || loading} onClick={() => setVersion(value => value + 1)}>Reload transaction</Button>
    {loading ? <p role="status">Loading transaction...</p> : detail && <>
      <h3>{formatMoney(detail.amount ?? detail.amountPaid, detail.currency)}</h3>
      <dl className="transaction-detail-grid">
        <div><dt>Customer</dt><dd>{getCustomerDisplayName(detail.user || {})}</dd></div>
        <div><dt>Payment status</dt><dd><span className="transaction-detail-status">{enumLabel(detail.paymentStatus)}</span></dd></div>
        <div><dt>Purpose</dt><dd><PurposeBadge value={detail.paymentType || detail.category} /></dd></div>
        <div><dt>Direction</dt><dd>{detail.transactionType ? enumLabel(detail.transactionType) : "—"}</dd></div>
        <div className="transaction-detail-wide"><dt>Reference</dt><dd>{detail.transactionReference}</dd></div>
        <div className="transaction-detail-wide"><dt>Created</dt><dd>{new Date(detail.createdAt).toLocaleString()}</dd></div>
      </dl>
      <div className="transaction-detail-actions">{can("transactions.export") && userId && <Button disabled={busy} onClick={download}>Customer transaction PDF</Button>}{can("wallet.refund") && refundable && <Button variant="danger" disabled={busy || uncertain} onClick={refund}>Refund wallet debit</Button>}</div>
    </>}
  </Modal>;
}
